/**
 * Still normalization for "Tela em branco": change aspect or unify resolution
 * without cropping. Same-ratio resizes stay local; a new aspect is filled by AI
 * outpainting (base with gray borders → image model → original pixels composited
 * back on top), so no pixel of the original is lost.
 */

export type GenAspect = '16:9' | '9:16' | '1:1' | '4:3' | '3:4';

/** Aspects the image models can output (create-preview). */
export const GEN_ASPECTS: GenAspect[] = ['16:9', '9:16', '1:1', '4:3', '3:4'];

const RATIOS: Record<GenAspect, number> = {
  '16:9': 16 / 9,
  '9:16': 9 / 16,
  '1:1': 1,
  '4:3': 4 / 3,
  '3:4': 3 / 4,
};

const TARGET_RATIOS: Record<string, number> = { ...RATIOS };

type Size = { width: number; height: number };

export function ratioFromLabel(label: string): number | null {
  return TARGET_RATIOS[label] ?? null;
}

async function loadImage(src: string): Promise<{ img: HTMLImageElement; revoke: () => void }> {
  let url = src;
  let revoke = () => {};
  if (/^https?:\/\//i.test(src)) {
    const res = await fetch(src);
    if (!res.ok) throw new Error(`Falha ao baixar imagem (${res.status}).`);
    url = URL.createObjectURL(await res.blob());
    revoke = () => URL.revokeObjectURL(url);
  }
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Falha ao carregar imagem.'));
    el.src = url;
  });
  return { img, revoke };
}

function naturalSize(img: HTMLImageElement): Size {
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  if (!width || !height) throw new Error('Imagem sem dimensões.');
  return { width, height };
}

function newCanvas(size: Size): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas indisponível.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return { canvas, ctx };
}

async function canvasToFile(canvas: HTMLCanvasElement, name: string, quality = 0.94): Promise<File> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Falha ao exportar imagem.'))), 'image/jpeg', quality);
  });
  return new File([blob], `${name}-${canvas.width}x${canvas.height}-${Date.now()}.jpg`, { type: 'image/jpeg' });
}

function scaledToMax(size: Size, maxEdge: number): Size {
  const s = Math.min(1, maxEdge / Math.max(size.width, size.height));
  return { width: Math.max(1, Math.round(size.width * s)), height: Math.max(1, Math.round(size.height * s)) };
}

/** Centered rect of `src` contained in `box`. */
function containRect(src: Size, box: Size): { x: number; y: number; width: number; height: number } {
  const c = Math.min(box.width / src.width, box.height / src.height);
  const width = Math.round(src.width * c);
  const height = Math.round(src.height * c);
  return { x: Math.round((box.width - width) / 2), y: Math.round((box.height - height) / 2), width, height };
}

export async function readImageSize(src: string): Promise<Size> {
  const { img, revoke } = await loadImage(src);
  try {
    return naturalSize(img);
  } finally {
    revoke();
  }
}

/** Target canvas that contains (w×h) at `ratio` without shrinking it. */
export function containCanvas(w: number, h: number, ratio: number): Size {
  if (w / h > ratio) return { width: w, height: Math.round(w / ratio) };
  return { width: Math.round(h * ratio), height: h };
}

/** True when fitting `src` into `target` leaves borders that must be invented. */
export function needsOutpaint(src: Size, target: Size): boolean {
  const a = src.width / src.height;
  const b = target.width / target.height;
  return Math.abs(a - b) / b > 0.005;
}

/** Small JPEG (base64, no data: prefix) for vision prompts — keeps request size bounded for any source size. */
export async function thumbnailBase64(src: string, maxEdge = 768, quality = 0.82): Promise<string> {
  const { img, revoke } = await loadImage(src);
  try {
    const size = scaledToMax(naturalSize(img), maxEdge);
    const { canvas, ctx } = newCanvas(size);
    ctx.drawImage(img, 0, 0, size.width, size.height);
    return canvas.toDataURL('image/jpeg', quality).split(',')[1] || '';
  } finally {
    revoke();
  }
}

/** Same-ratio resize (no new content): original drawn to the exact target size. */
export async function resizeStill(src: string, target: Size, maxEdge = 2560): Promise<{ file: File } & Size> {
  const size = scaledToMax(target, maxEdge);
  const { img, revoke } = await loadImage(src);
  try {
    const { canvas, ctx } = newCanvas(size);
    ctx.drawImage(img, 0, 0, size.width, size.height);
    return { file: await canvasToFile(canvas, 'tela'), ...size };
  } finally {
    revoke();
  }
}

type Rect = { x: number; y: number; width: number; height: number };

/** Photo area inside flat letterbox/pillarbox bars (gray/black padding baked into the file). */
function contentRectOf(img: HTMLImageElement): Rect {
  const s = naturalSize(img);
  const probe = scaledToMax(s, 320);
  const { ctx } = newCanvas(probe);
  ctx.drawImage(img, 0, 0, probe.width, probe.height);
  const { data } = ctx.getImageData(0, 0, probe.width, probe.height);
  const px = (x: number, y: number) => {
    const i = (y * probe.width + x) * 4;
    return [data[i], data[i + 1], data[i + 2]];
  };
  const flatLine = (count: number, at: (k: number) => number[]) => {
    const first = at(0);
    if (Math.max(...first) - Math.min(...first) > 14) return false;
    for (let k = 1; k < count; k++) {
      const c = at(k);
      if (Math.max(...c) - Math.min(...c) > 14) return false;
      if (Math.abs(c[0] - first[0]) > 14 || Math.abs(c[1] - first[1]) > 14 || Math.abs(c[2] - first[2]) > 14) return false;
    }
    return true;
  };
  const col = (x: number) => flatLine(probe.height, (y) => px(x, y));
  const row = (y: number) => flatLine(probe.width, (x) => px(x, y));
  let left = 0;
  while (left < probe.width / 3 && col(left)) left++;
  let right = 0;
  while (right < probe.width / 3 && col(probe.width - 1 - right)) right++;
  let top = 0;
  while (top < probe.height / 3 && row(top)) top++;
  let bottom = 0;
  while (bottom < probe.height / 3 && row(probe.height - 1 - bottom)) bottom++;
  const minBar = 0.01;
  const k = s.width / probe.width;
  const same = (a: number[], b: number[]) => a.every((v, i) => Math.abs(v - b[i]) <= 14);
  const trimX = left / probe.width > minBar && right / probe.width > minBar &&
    same(px(0, probe.height >> 1), px(probe.width - 1, probe.height >> 1));
  const trimY = top / probe.height > minBar && bottom / probe.height > minBar &&
    same(px(probe.width >> 1, 0), px(probe.width >> 1, probe.height - 1));
  // +1 probe px inset hides the JPEG seam between bar and photo.
  const x = trimX ? Math.ceil((left + 1) * k) : 0;
  const y = trimY ? Math.ceil((top + 1) * (s.height / probe.height)) : 0;
  const width = trimX ? Math.floor((probe.width - right - 1) * k) - x : s.width;
  const height = trimY ? Math.floor((probe.height - bottom - 1) * (s.height / probe.height)) - y : s.height;
  return { x, y, width, height };
}

/** Size of the real photo (ignores flat bars baked into the file). */
export async function readContentSize(src: string): Promise<Size> {
  const { img, revoke } = await loadImage(src);
  try {
    const r = contentRectOf(img);
    return { width: r.width, height: r.height };
  } finally {
    revoke();
  }
}

/** True when the image still has flat bars on both sides of an axis (e.g. outpaint that kept the gray). */
export async function hasBakedBars(src: string): Promise<boolean> {
  const { img, revoke } = await loadImage(src);
  try {
    const s = naturalSize(img);
    const r = contentRectOf(img);
    return r.width < s.width * 0.98 || r.height < s.height * 0.98;
  } finally {
    revoke();
  }
}

/** Whether the image at `src` has the shape of `aspect` (within `tolerance`, relative). */
export async function hasAspect(src: string, aspect: GenAspect, tolerance = 0.04): Promise<boolean> {
  const { img, revoke } = await loadImage(src);
  try {
    const s = naturalSize(img);
    return Math.abs(s.width / s.height / RATIOS[aspect] - 1) <= tolerance;
  } finally {
    revoke();
  }
}

/**
 * Centered crop of the real photo (bars dropped) that fills `ratio` — drops edges instead of inventing them.
 * `ratio` null keeps the photo's own ratio (only strips the bars).
 */
export async function cropStillToRatio(src: string, ratio: number | null, maxEdge = 2560): Promise<{ file: File } & Size> {
  const { img, revoke } = await loadImage(src);
  try {
    const box = contentRectOf(img);
    if (ratio == null) ratio = box.width / box.height;
    const crop = box.width / box.height > ratio
      ? { width: Math.round(box.height * ratio), height: box.height }
      : { width: box.width, height: Math.round(box.width / ratio) };
    const size = scaledToMax(crop, maxEdge);
    const { canvas, ctx } = newCanvas(size);
    ctx.drawImage(
      img,
      box.x + Math.round((box.width - crop.width) / 2),
      box.y + Math.round((box.height - crop.height) / 2),
      crop.width,
      crop.height,
      0,
      0,
      size.width,
      size.height,
    );
    return { file: await canvasToFile(canvas, 'tela'), ...size };
  } finally {
    revoke();
  }
}

/** Where the largest `ratio` window sits: `ox`/`oy` 0–1 along the free space of each axis. */
export type CropPick = { ox: number; oy: number };
export const CENTER_CROP: CropPick = { ox: 0.5, oy: 0.5 };

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** Largest window (natural px) of `ratio` inside `src`, placed by `pick`. */
export function cropRectFor(src: Size, ratio: number, pick: CropPick): Rect {
  const { width, height } = src.width / src.height > ratio
    ? { width: src.height * ratio, height: src.height }
    : { width: src.width, height: src.width / ratio };
  return { x: (src.width - width) * clamp01(pick.ox), y: (src.height - height) * clamp01(pick.oy), width, height };
}

/** Crop chosen by the user (no AI): the window from `cropRectFor`, at its own resolution. */
export async function cropStillRect(src: string, ratio: number, pick: CropPick, maxEdge = 2560): Promise<{ file: File } & Size> {
  const { img, revoke } = await loadImage(src);
  try {
    const r = cropRectFor(naturalSize(img), ratio, pick);
    const size = scaledToMax({ width: Math.round(r.width), height: Math.round(r.height) }, maxEdge);
    const { canvas, ctx } = newCanvas(size);
    ctx.drawImage(img, r.x, r.y, r.width, r.height, 0, 0, size.width, size.height);
    return { file: await canvasToFile(canvas, 'tela'), ...size };
  } finally {
    revoke();
  }
}

/** Generation aspect whose canvas holds `frame` with the least extra area. */
export function outpaintAspectFor(frame: Size): GenAspect {
  const r = frame.width / frame.height;
  return GEN_ASPECTS.reduce((best, cur) =>
    Math.abs(Math.log(RATIOS[cur] / r)) < Math.abs(Math.log(RATIOS[best] / r)) ? cur : best,
  );
}

/**
 * Input for the image model: original centered on a flat gray canvas at a
 * generation aspect that contains `frame`.
 */
export async function renderOutpaintBase(
  src: string,
  frame: Size,
  maxEdge = 2048,
): Promise<{ file: File; aspect: GenAspect }> {
  const aspect = outpaintAspectFor(frame);
  const full = containCanvas(frame.width, frame.height, RATIOS[aspect]);
  const size = scaledToMax(full, maxEdge);
  const s = size.width / full.width;
  const { img, revoke } = await loadImage(src);
  try {
    const srcSize = naturalSize(img);
    const inFrame = containRect(srcSize, frame);
    const { canvas, ctx } = newCanvas(size);
    ctx.fillStyle = '#808080';
    ctx.fillRect(0, 0, size.width, size.height);
    const w = inFrame.width * s;
    const h = inFrame.height * s;
    ctx.drawImage(img, (size.width - w) / 2, (size.height - h) / 2, w, h);
    return { file: await canvasToFile(canvas, 'tela-base', 0.92), aspect };
  } finally {
    revoke();
  }
}

/**
 * Final still at `frame` size: AI result fills the borders, the original is
 * drawn back on top with a short feather only on edges that touch AI content.
 */
export async function composeOutpaint(
  src: string,
  aiUrl: string,
  frame: Size,
  aspect: GenAspect,
  maxEdge = 2560,
): Promise<{ file: File } & Size> {
  const size = scaledToMax(frame, maxEdge);
  const f = size.width / frame.width;
  const full = containCanvas(frame.width, frame.height, RATIOS[aspect]);
  const [orig, ai] = await Promise.all([loadImage(src), loadImage(aiUrl)]);
  try {
    const { canvas, ctx } = newCanvas(size);
    const fullW = full.width * f;
    const fullH = full.height * f;
    ctx.drawImage(ai.img, (size.width - fullW) / 2, (size.height - fullH) / 2, fullW, fullH);

    const rect = containRect(naturalSize(orig.img), size);
    const layer = newCanvas({ width: rect.width, height: rect.height });
    layer.ctx.drawImage(orig.img, 0, 0, rect.width, rect.height);
    const feather = Math.max(4, Math.round(Math.min(size.width, size.height) * 0.012));
    layer.ctx.globalCompositeOperation = 'destination-out';
    const fade = (x0: number, y0: number, x1: number, y1: number, rx: number, ry: number, rw: number, rh: number) => {
      const g = layer.ctx.createLinearGradient(x0, y0, x1, y1);
      g.addColorStop(0, 'rgba(0,0,0,1)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      layer.ctx.fillStyle = g;
      layer.ctx.fillRect(rx, ry, rw, rh);
    };
    if (rect.x > 1) {
      fade(0, 0, feather, 0, 0, 0, feather, rect.height);
      fade(rect.width, 0, rect.width - feather, 0, rect.width - feather, 0, feather, rect.height);
    }
    if (rect.y > 1) {
      fade(0, 0, 0, feather, 0, 0, rect.width, feather);
      fade(0, rect.height, 0, rect.height - feather, 0, rect.height - feather, rect.width, feather);
    }
    ctx.drawImage(layer.canvas, rect.x, rect.y);
    return { file: await canvasToFile(canvas, 'tela'), ...size };
  } finally {
    orig.revoke();
    ai.revoke();
  }
}

/**
 * Plan target sizes for a batch: with `aspect`, each image fits that ratio;
 * with `matchResolution`, all share the largest resulting canvas.
 */
export function planNormalizedSizes(
  sizes: Size[],
  opts: { aspect?: string; matchResolution?: boolean },
): Size[] {
  const ratio = opts.aspect ? ratioFromLabel(opts.aspect) : null;
  const perImage = sizes.map((s) => (ratio ? containCanvas(s.width, s.height, ratio) : { ...s }));
  if (!opts.matchResolution || !perImage.length) return perImage;

  const commonRatio = ratio ?? mostCommonRatio(sizes);
  const contained = sizes.map((s) => containCanvas(s.width, s.height, commonRatio));
  const width = Math.max(...contained.map((c) => c.width));
  const shared = { width, height: Math.round(width / commonRatio) };
  return sizes.map(() => shared);
}

function mostCommonRatio(sizes: Size[]): number {
  const labels = Object.keys(TARGET_RATIOS);
  const votes = new Map<string, number>();
  for (const s of sizes) {
    const r = s.width / s.height;
    const label = labels.reduce((best, cur) =>
      Math.abs(TARGET_RATIOS[cur] - r) < Math.abs(TARGET_RATIOS[best] - r) ? cur : best,
    );
    votes.set(label, (votes.get(label) || 0) + 1);
  }
  const winner = Array.from(votes.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '16:9';
  return TARGET_RATIOS[winner];
}

export function sameSize(a: Size, b: Size) {
  return Math.abs(a.width - b.width) <= 1 && Math.abs(a.height - b.height) <= 1;
}
